import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { gradients } from '../../theme/designTokens';

const FloatingOrb = ({ size, top, left, right, bottom, color = 'blue', delay = 0 }) => {
  const orbColor =
    color === 'gold'
      ? 'radial-gradient(circle, rgba(238,186,43,0.2) 0%, transparent 70%)'
      : 'radial-gradient(circle, rgba(59,130,246,0.22) 0%, transparent 70%)';

  return (
    <Box
      component={motion.div}
      animate={{ y: [0, -18, 0], opacity: [0.4, 0.75, 0.4], scale: [1, 1.08, 1] }}
      transition={{ duration: 6 + delay, repeat: Infinity, ease: 'easeInOut', delay }}
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: '50%',
        top,
        left,
        right,
        bottom,
        background: orbColor,
        pointerEvents: 'none',
        filter: 'blur(1px)',
      }}
    />
  );
};

const AnimatedGrid = ({ dark = false }) => (
  <Box
    component={motion.div}
    animate={{ backgroundPosition: ['0px 0px', '48px 48px'] }}
    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
    sx={{
      position: 'absolute',
      inset: 0,
      backgroundImage: dark
        ? `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
           linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`
        : `linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px),
           linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)`,
      backgroundSize: '48px 48px',
      pointerEvents: 'none',
      opacity: 0.7,
    }}
  />
);

const MeshGradient = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      background: gradients.mesh,
      pointerEvents: 'none',
    }}
  />
);

const MouseSpotlight = ({ dark = false }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      setVisible(true);
    };
    const onLeave = () => setVisible(false);

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: pos.x,
          top: pos.y,
          width: 500,
          height: 500,
          transform: 'translate(-50%, -50%)',
          borderRadius: '50%',
          background: dark
            ? 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      />
    </Box>
  );
};

const BackgroundEffects = ({
  variant = 'light',
  orbs = true,
  grid = true,
  mesh = true,
  spotlight = true,
  children,
  sx = {},
}) => {
  const isDark = variant === 'dark';

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: isDark ? '#020817' : 'transparent',
        background: isDark ? gradients.hero : 'transparent',
        ...sx,
      }}
    >
      {mesh && !isDark && <MeshGradient />}
      {grid && <AnimatedGrid dark={isDark} />}
      {spotlight && <MouseSpotlight dark={isDark} />}
      {orbs && (
        <>
          <FloatingOrb size={400} top="-15%" right="-8%" delay={0} color="blue" />
          <FloatingOrb size={280} bottom="0%" left="-10%" delay={1.5} color="gold" />
          <FloatingOrb size={200} top="50%" left="40%" delay={0.8} color="blue" />
        </>
      )}
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>{children}</Box>
    </Box>
  );
};

export { FloatingOrb, AnimatedGrid, MeshGradient, MouseSpotlight };
export default BackgroundEffects;
